import React, {
  useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { Header, Message, Confirm } from 'semantic-ui-react';
import { setPage } from '../data/page/actions';
import VRAPendingList from '../views/VRAPending/VRAPendingList';
import VRAPendingDetails from '../views/VRAPending/VRAPendingDetails';
import InvalidatePendingForm from '../views/VRAPending/InvalidatePendingForm';
import ConfirmGuaranteeExtension, { GRACE_PERIOD_DAYS } from '../views/VRAPending/ConfirmGuaranteeExtension';
import ReturnInvoiceEdit from '../views/ReturnInvoices/Edit';
import pendingReturnInvoicesQuery from '../graphql/pendingReturnInvoicesQuery';
import pendingVRAMutation from '../graphql/pendingVRAMutation';
import updateGuaranteePurchaseMutation from '../graphql/updateGuaranteePurchaseMutation';
import guaranteeExtensionEligibilityQuery from '../graphql/guaranteeExtensionEligibilityQuery';
import updateReturnInvoiceMutation from '../graphql/updateReturnInvoiceMutation';
import VRAPendingSearch from '../views/VRAPending/VRAPendingSearch';
import client from '../apollo';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import { isExpired } from '../utilities/dateUtils';

const VRA_ACTION_MESSAGES = {
  ALLOW: {
    success: 'Successfully Approved the VRA pending',
    error: 'Failed to process the Allow action for the Pending VRA. Please reach out to the DealShield team.',
  },
  REJECT: {
    success: 'Successfully Invalidated the VRA Pending.',
    error: 'Failed to process the Invalidate action for the Pending VRA. Please reach out to the DealShield team.',
  },
  EXPIRED: {
    error: 'This action cannot be completed because the return date has expired',
  },
};

export const calculateExtensionDays = expirationDate => moment.utc()
  .startOf('day')
  .add(GRACE_PERIOD_DAYS, 'days')
  .diff(moment.utc(expirationDate).startOf('day'), 'days');

const buildGuaranteePurchaseExtensionInput = (pendingVRA, extensionDays) => {
  const guaranteePurchase = pendingVRA?.guaranteePurchase ?? {};
  const guaranteeOffer = guaranteePurchase.guaranteeOffer ?? {};
  const vehicleInformation = guaranteeOffer.vehicleInformation ?? {};
  const auctionInformation = guaranteeOffer.auctionInformation ?? {};

  return {
    id: parseInt(guaranteePurchase.id, 10),
    dealerNumber: parseInt(auctionInformation.dealerNumber, 10),
    auctionAccessNumber: parseInt(auctionInformation.auctionAccessNumber, 10),
    buyFee: parseFloat(auctionInformation.buyFee || 0),
    vehiclePurchasePrice: parseFloat(auctionInformation.vehiclePurchasePrice),
    year: parseInt(vehicleInformation.year, 10),
    make: vehicleInformation.make,
    model: vehicleInformation.model || '',
    odometerReading: parseInt(vehicleInformation.odometerReading, 10),
    partner: guaranteePurchase.partner || '',
    kind: guaranteePurchase.kind,
    milesSelected: parseInt(guaranteeOffer.milesSelected, 10),
    daysSelected: parseInt(guaranteeOffer.daysSelected, 10),
    transportReimbursement: parseFloat(guaranteeOffer.transportReimbursement),
    emails: guaranteeOffer.emails,
    guaranteedAt: guaranteePurchase.guaranteedAt,
    guaranteePrice: parseFloat(guaranteeOffer.guaranteePrice || 0),
    extensionReason: '',
    extensionDays,
  };
};

export const VRAPending = props => {
  const {
    data: { pendingReturnInvoices, refetch },
    pendingVRAMutation: submitPendingVRAMutation,
    updateGuaranteePurchaseMutation: submitUpdateGuaranteePurchaseMutation,
    setPageAction,
  } = props;

  const [selectedVRAPending, setSelectedVRAPending] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isExtensionConfirmOpen, setIsExtensionConfirmOpen] = useState(false);
  const [extensionDays, setExtensionDays] = useState(null);
  const [isExtendingGuarantee, setIsExtendingGuarantee] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [error, setError] = useState(null);
  const [detailsError, setDetailsError] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isInvalidateModalOpen, setIsInvalidateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const onSearchSubmitHandler = useCallback(event => {
    const { dealerNumber, vin, vra } = event.target;
    refetch({
      dealerNumber: dealerNumber.value.trim() || null,
      vin: vin.value.trim() || null,
      vra: vra.value.trim() || null,
    });
    setPageAction({});
  }, [refetch, setPageAction]);

  const resetVRASelection = () => {
    setIsDetailsModalOpen(false);
    setIsConfirmModalOpen(false);
    setIsExtensionConfirmOpen(false);
    setIsInvalidateModalOpen(false);
    setIsEditModalOpen(false);
    setExtensionDays(null);
    setDetailsError(null);
    setSelectedVRAPending(null);
  };

  const submitPendingVRAAction = (pendingVRA, mutationAction, extraInput = {}) => {
    const { success: successMessage, error: errorMessage } = VRA_ACTION_MESSAGES[mutationAction];
    const mutationPromise = submitPendingVRAMutation({
      variables: {
        input: {
          pendingVra: { vraId: pendingVRA.id, action: mutationAction, ...extraInput },
        },
      },
    });
    mutationPromise
      .then(({ data: { pendingVra } }) => {
        const { success } = pendingVra;
        refetch();
        if (success) {
          setFlashMessage(successMessage);
          setError(null);
        } else {
          setFlashMessage(null);
          setError(errorMessage);
        }
      })
      .catch(() => {
        resetVRASelection();
        setFlashMessage(null);
        setError(errorMessage);
      });
  };

  const onConfirmModalCancelHandler = () => {
    resetVRASelection();
  };

  const onConfirmModalConfirmHandler = () => {
    if (isExpired(selectedVRAPending.expirationDate)) {
      setFlashMessage(null);
      setError(VRA_ACTION_MESSAGES.EXPIRED.error);
    } else {
      submitPendingVRAAction(selectedVRAPending, 'ALLOW');
    }
    resetVRASelection();
  };

  const onInvalidateModalCancelHandler = () => {
    setIsInvalidateModalOpen(false);
  };

  const onInvalidateModalSubmitHandler = ({ reason }) => {
    submitPendingVRAAction(selectedVRAPending, 'REJECT', { reason });
    resetVRASelection();
  };

  const onActionHandler = (action, pendingVRA) => {
    if (!action || !pendingVRA) return;
    switch (action) {
      case 'view':
        setSelectedVRAPending(pendingVRA);
        setIsDetailsModalOpen(true);
        break;
      case 'allow':
        setSelectedVRAPending(pendingVRA);
        if (!isExpired(pendingVRA.expirationDate) && isExpired(pendingVRA.guaranteePurchase?.expiresAt)) {
          setExtensionDays(calculateExtensionDays(pendingVRA.guaranteePurchase.expiresAt));
          setIsExtensionConfirmOpen(true);
        } else {
          setIsConfirmModalOpen(true);
        }
        break;
      case 'invalidate':
        setSelectedVRAPending(pendingVRA);
        setIsInvalidateModalOpen(true);
        break;
      case 'edit':
        setSelectedVRAPending(pendingVRA);
        setIsEditModalOpen(true);
        break;
      default:
        break;
    }
  };

  const onEditCloseHandler = () => {
    resetVRASelection();
  };

  const onEditSubmitHandler = values => {
    setIsEditSubmitting(true);
    const { updateReturnInvoiceMutation: submitUpdateReturnInvoiceMutation } = props;
    submitUpdateReturnInvoiceMutation({
      variables: { input: { returnInvoice: { ...values } } },
    })
      .then(({ data: { updateReturnInvoice } }) => {
        const { success, errors } = updateReturnInvoice;
        setIsEditSubmitting(false);
        if (success) {
          refetch();
          resetVRASelection();
          setFlashMessage('Successfully Edited VRA Pending');
          setError(null);
        } else {
          resetVRASelection();
          setFlashMessage(null);
          setError(errors);
        }
      })
      .catch(() => {
        setIsEditSubmitting(false);
        resetVRASelection();
        setFlashMessage(null);
        setError('Error updating VRA pending');
      });
  };

  const allowPendingVRA = () => {
    if (!selectedVRAPending) return;
    if (isExpired(selectedVRAPending.expirationDate)) {
      setDetailsError(VRA_ACTION_MESSAGES.EXPIRED.error);
      return;
    }
    const guaranteeExpiresAt = selectedVRAPending.guaranteePurchase?.expiresAt;
    if (isExpired(guaranteeExpiresAt)) {
      setExtensionDays(calculateExtensionDays(guaranteeExpiresAt));
      setIsExtensionConfirmOpen(true);
    } else {
      submitPendingVRAAction(selectedVRAPending, 'ALLOW');
      resetVRASelection();
    }
  };

  const invalidatePendingVRA = () => {
    onActionHandler('invalidate', selectedVRAPending);
  };

  const onExtensionBackHandler = () => {
    setIsExtensionConfirmOpen(false);
    setExtensionDays(null);
  };

  const onExtensionProceedHandler = async() => {
    const { error: allowErrorMessage } = VRA_ACTION_MESSAGES.ALLOW;
    const guaranteePurchase = selectedVRAPending?.guaranteePurchase;
    setIsExtendingGuarantee(true);
    try {
      const { data: eligibilityData } = await client.query({
        query: guaranteeExtensionEligibilityQuery,
        variables: { guaranteePurchaseId: String(guaranteePurchase?.id) },
        fetchPolicy: 'network-only',
      });
      const eligibility = eligibilityData?.extensionEligibility;
      if (!eligibility) {
        throw new Error('Extension eligibility unavailable');
      }
      if (!eligibility.allowed) {
        setIsExtendingGuarantee(false);
        setFlashMessage(null);
        setError(eligibility.reason || VRA_ACTION_MESSAGES.EXPIRED.error);
        resetVRASelection();
        return;
      }

      const { data: { updateGuaranteePurchase } } = await submitUpdateGuaranteePurchaseMutation({
        variables: {
          purchaseInput: {
            guaranteePurchase: buildGuaranteePurchaseExtensionInput(selectedVRAPending, extensionDays),
          },
        },
      });
      setIsExtendingGuarantee(false);
      if (updateGuaranteePurchase.success) {
        submitPendingVRAAction(selectedVRAPending, 'ALLOW');
        resetVRASelection();
      } else {
        setFlashMessage(null);
        setError(allowErrorMessage);
        resetVRASelection();
      }
    } catch (e) {
      setIsExtendingGuarantee(false);
      setFlashMessage(null);
      setError(allowErrorMessage);
    }
  };

  return (
    <div>
      <Header>VRA Pending</Header>
      {flashMessage && (
        <Message
          onDismiss={() => setFlashMessage(null)}
          content={flashMessage}
          positive
        />
      )}
      {error && (
        <Message
          onDismiss={() => setError(null)}
          content={error}
          negative
        />
      )}
      <VRAPendingSearch
        onSubmit={onSearchSubmitHandler}
        searchFields={{}}
        searchOptions={['dealerNumber', 'vin', 'vra']}
      />
      <VRAPendingList
        VRAsPending={pendingReturnInvoices}
        actionHandler={onActionHandler}
      />
      {selectedVRAPending && isDetailsModalOpen && (
        <VRAPendingDetails
          pendingVRA={selectedVRAPending}
          isDetailsModalOpen={isDetailsModalOpen}
          onCloseHandler={resetVRASelection}
          allowPendingVRA={allowPendingVRA}
          errorMessage={detailsError}
          onErrorDismiss={() => setDetailsError(null)}
          invalidatePendingVRA={invalidatePendingVRA}
        />
      )}
      {selectedVRAPending && isInvalidateModalOpen && (
        <InvalidatePendingForm
          isInvalidateModalOpen={isInvalidateModalOpen}
          onCloseHandler={onInvalidateModalCancelHandler}
          onSubmitHandler={onInvalidateModalSubmitHandler}
        />
      )}
      {selectedVRAPending && isEditModalOpen && (
        <ReturnInvoiceEdit
          returnInvoice={selectedVRAPending}
          isEditModalOpen={isEditModalOpen}
          onCloseHandler={onEditCloseHandler}
          onSubmitHandler={onEditSubmitHandler}
          submitting={isEditSubmitting}
        />
      )}
      {selectedVRAPending && isExtensionConfirmOpen && (
        <ConfirmGuaranteeExtension
          pendingVRA={selectedVRAPending}
          extensionDays={extensionDays}
          open={isExtensionConfirmOpen}
          onBackHandler={onExtensionBackHandler}
          onProceedHandler={onExtensionProceedHandler}
          submitting={isExtendingGuarantee}
        />
      )}
      <Confirm
        open={isConfirmModalOpen}
        onCancel={onConfirmModalCancelHandler}
        onConfirm={onConfirmModalConfirmHandler}
      />
    </div>
  );
};

const mapStateToProps = () => {

};

const mapDispatchToProps = dispatch => ({
  setPageAction: () => dispatch(setPage({})),
});

VRAPending.propTypes = {
  data: PropTypes.shape({
    pendingReturnInvoices: PropTypes.array,
    refetch: PropTypes.func,
  }),
  pendingVRAMutation: PropTypes.func.isRequired,
  updateGuaranteePurchaseMutation: PropTypes.func.isRequired,
  updateReturnInvoiceMutation: PropTypes.func.isRequired,
  setPageAction: PropTypes.func,
};

export default compose(
  withRouter,
  withPagination(pendingReturnInvoicesQuery, 'pendingReturnInvoices'),
  graphql(pendingReturnInvoicesQuery, { name: 'pendingReturnInvoicesData' }),
  graphql(pendingVRAMutation, { name: 'pendingVRAMutation' }),
  graphql(updateGuaranteePurchaseMutation, { name: 'updateGuaranteePurchaseMutation' }),
  graphql(updateReturnInvoiceMutation, { name: 'updateReturnInvoiceMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(VRAPending);
